import { useCallback } from 'react';
import { useAppStore } from '@/stores/appStore';
import { apiClient } from '@/utils/apiClient';

export const useMCPServers = () => {
  const { mcpServers, updateMCPServer, setError } = useAppStore();

  const normalizeName = (name: string) => name.trim().toLowerCase();
  const normalizeUrl = (url: string) => url.replace(/\/?$/, '/');

  const refreshServerStatus = useCallback(async (refresh: boolean = false) => {
    // Skip if auth token isn't wired yet — avoids console errors during init race
    if (!apiClient.getAuthToken()) return;

    try {
      const serverInfos = await apiClient.getMCPServers(refresh);

      // Create a set of server names/URLs from the backend response
      const respondedServers = new Set(
        serverInfos.map(s => normalizeName(s.name))
      );
      
      // Update server status in the store (preserve favorites on tools)
      for (const serverInfo of serverInfos) {
        const existingServer =
          // exact name match first
          mcpServers.find(s => s.name === serverInfo.name)
          // case-insensitive name match
          || mcpServers.find(s => normalizeName(s.name) === normalizeName(serverInfo.name))
          // url match fallback (normalize trailing slash)
          || mcpServers.find(s => normalizeUrl(s.url) === normalizeUrl(serverInfo.url));
        if (existingServer) {
          const prevTools = existingServer.tools || [];
          const mergedTools = serverInfo.tools?.map(tool => {
            const prev = prevTools.find(t => t.name === tool.name) as any;
            return {
              name: tool.name,
              description: tool.description,
              inputSchema: {},
              favorite: prev?.favorite || false,
            } as any;
          });

          // Only update the store if something actually changed — avoids
          // re-renders that cause the UI to flash on every poll cycle.
          const newStatus = serverInfo.status as any;
          const newCount = mergedTools?.length ?? 0;
          const oldCount = prevTools.length;
          const statusChanged = existingServer.status !== newStatus;
          const toolsChanged = newCount !== oldCount;
          if (statusChanged || toolsChanged) {
            updateMCPServer(existingServer.id, {
              status: newStatus,
              tools: mergedTools,
            });
          }
        }
      }
      
      // Mark servers that didn't respond as disconnected
      for (const server of mcpServers) {
        const normalized = normalizeName(server.name);
        if (!respondedServers.has(normalized) && server.status !== 'disconnected') {
          console.log(`Marking ${server.name} as disconnected (no response from backend)`);
          updateMCPServer(server.id, {
            status: 'disconnected',
            tools: [],
          });
        }
      }
    } catch (error) {
      // Transient failures during initialization are expected — don't alarm users
      console.warn('Failed to refresh server status:', error);
    }
  }, [mcpServers, updateMCPServer, setError]);

  const checkBackendHealth = useCallback(async () => {
    try {
      const health = await apiClient.healthCheck();
      return health;
    } catch (error) {
      console.error('Backend health check failed:', error);
      return null;
    }
  }, []);

  return {
    refreshServerStatus,
    checkBackendHealth,
  };
};